// Full §5.3 result schema (spec "Ticket, session contract và verifier",
// "5.3 Kết quả worker — đề nghị hoàn thành"). Review fix (code-reviewer
// 260909-p4-cli-schemas, must-fix High): the previous version was a P2
// placeholder (`ok`+`summary`, passthrough) that never validated `outcome`
// — replaced with the full strict schema below. Exports (`AutopilotResultSchema`,
// `AUTOPILOT_RESULT_JSON_SCHEMA_STRING`, `AutopilotResult` type) kept
// name-for-name so P2 consumers (terminal-result-extractor.ts,
// claude-headless-argv-builder.ts) need no import changes.
import { z } from "zod";
import { omitControllerOwnedResultFields } from "./result-json-schema-controller-owned-fields.js";

const SHA256_HEX = /^[0-9a-f]{64}$/i;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// base_sha: full 40-char SHA is common, but git also accepts abbreviated
// hashes down to 7 chars in normal usage — spec text says "hex 7-40".
const BASE_SHA_RE = /^[0-9a-f]{7,40}$/i;

const EvidenceSchema = z
  .object({
    recipe_id: z.string().min(1),
    report_path: z.string().min(1),
    sha256: z.string().regex(SHA256_HEX),
    exit_code: z.number().int(),
  })
  .strict();

const ProposedChangeSchema = z
  .object({
    op: z.enum(["create", "amend", "cancel", "supersede"]),
    target: z.string().min(1),
    expected_revision: z.number().int().nonnegative(),
    replacement_ids: z.array(z.string()).optional(),
    content: z.unknown().optional(),
  })
  .strict();

// P21 (layer C): the review report a `review-local-v1` session produces,
// carried in the result itself instead of only as a file the worker may or
// may not write. P20's real mission proved the file route alone has no
// teeth: the worker had the playbook, the `Write(.avatar/attempt-output/**)`
// grant and no deny rule, wrote nothing, and the ticket still went `done`
// because nothing verified it had.
//
// OPTIONAL here on purpose — this schema is shared by all nine adapters
// (command-adapter-table.ts), so requiring the field outright would
// `contract_error` every implement/testcase/survey attempt. The requirement
// for review-local attempts specifically lives in the verifier
// (result-verifier.ts step 6c, `isReviewLocalAttempt`), which is the layer
// that knows which adapter ran. What the schema DOES enforce is the shape:
// `risk` is an enum, so a worker cannot discharge the obligation with a
// plausible-sounding word like "ok" — same mechanism that made a real
// worker refuse to fabricate `input_hash` in D08.
const ReviewReportSchema = z
  .object({
    risk: z.enum(["low", "medium", "high", "critical"]),
    secret_scan: z
      .object({
        verdict: z.string().min(1),
        findings: z.number().int().nonnegative(),
      })
      .strict(),
    evidence_hashes: z.array(z.string()),
  })
  .strict();

export const AutopilotResultSchema = z
  .object({
    schema_version: z.literal(1),
    mission_id: z.string().min(1),
    ticket_id: z.string().min(1),
    ticket_revision: z.number().int().min(1),
    run_id: z.string().regex(UUID_RE),
    session_id: z.string().regex(UUID_RE),
    attempt_no: z.number().int().min(1),
    // D08 (dogfood vòng 4): `input_hash` is the CONTROLLER's field, not the
    // worker's — controller computes it at dispatch (see
    // `computeControllerInputHash` in canonical-json-and-checksum.ts) from
    // data only the controller holds (run_id/ticket_revision/prompt_sha256
    // BEFORE the worker ever sees the prompt). No command adapter grants a
    // hash tool (0/9 in command-adapter-table.ts — confirmed by dogfood
    // vòng 3's real-worker evidence), so requiring the worker to self-attest
    // this value forced every real attempt to either fabricate it (unsafe)
    // or refuse (blocking every mission). Optional here; regex still
    // enforced IF the worker chooses to send one, so a mismatched value is
    // still catchable by the controller's own compare-and-reject step.
    input_hash: z.string().regex(SHA256_HEX).optional(),
    base_sha: z.string().regex(BASE_SHA_RE),
    outcome: z.enum(["done", "failed", "blocked", "needs_decision"]),
    error_class: z.string().nullable(),
    summary: z.string().min(1),
    // "changed_files": relative paths (never absolute — worker never sees
    // outside allowed_paths/repo_root, so a leading "/" would already be a
    // scope violation the schema itself should refuse to model as valid).
    changed_files: z.array(z.string().refine((p) => !p.startsWith("/"), "phải là relative path")),
    evidence: z.array(EvidenceSchema),
    // P21: review-local-v1's one obligation. NOT part of `evidence[]` —
    // that array means "I ran approved recipe X" and the verifier re-runs
    // each entry, rejecting recipes absent from the verification-profile
    // (measured in P20: routing the report through `evidence[]` fails with
    // `recipe "review-bootstrap" không có trong verification-profile đã duyệt`).
    review_report: ReviewReportSchema.optional(),
    // A2 (plan 20260915-1650 phase-01, spec §A2 "Chưa kiểm chứng" / "Còn
    // lại"): the report's coverage limit — what the worker did NOT verify,
    // and what is left for a person (work, or a permission to grant).
    // OPTIONAL for the same reason review_report is: this strict schema is
    // shared by every adapter and by the e2e fake-worker, so a required
    // field would `contract_error` all of them. Entries must be non-empty
    // strings so `[""]` cannot discharge the obligation. Journaled on the
    // done `attempt_finished` (supervisor-loop-outcome-journal.ts) and
    // rendered into the candidate MR description; NEVER a substitute for
    // `evidence[]` (result-verifier-checks.ts's EVIDENCE_REQUIRED stays).
    uncovered_areas: z.array(z.string().min(1)).optional(),
    remaining_work: z.array(z.string().min(1)).optional(),
    proposed_tickets: z.array(z.record(z.string(), z.unknown())),
    proposed_changes: z.array(ProposedChangeSchema),
    decisions: z.array(z.record(z.string(), z.unknown())),
    options: z.array(z.record(z.string(), z.unknown())),
    sources: z.array(z.string()),
  })
  .strict()
  // "blocked cần reason/capability/request" (§5.3) — §5.3's base shape has
  // no dedicated reason/capability/request field, so the worker's reason
  // for being blocked is required to land in `error_class` (the one
  // free-text-ish field the schema has for "why"). A blocked result with
  // error_class:null is exactly the shape the spec calls out as invalid.
  .refine((result) => result.outcome !== "blocked" || result.error_class !== null, {
    message: "outcome=blocked yêu cầu error_class mô tả reason/capability/request đang thiếu",
  })
  .refine((result) => result.outcome !== "needs_decision" || result.options.length > 0, {
    message: "outcome=needs_decision yêu cầu options[] không rỗng",
  });

export type AutopilotResult = z.infer<typeof AutopilotResultSchema>;

// zod v4.4.3 ships `z.toJSONSchema` — verified present in this repo's
// installed version (no fallback needed). Exported as a pre-serialized
// string because the argv builder needs `--json-schema <string>` verbatim.
//
// `$schema` is dropped (F1, plan 20260924-0150): z.toJSONSchema stamps the
// draft 2020-12 meta-schema URI, and claude 2.1.278 rejects `--json-schema`
// with "no schema with key or ref …/draft/2020-12/schema" before emitting any
// event — every worker session of mission ME-2026-6d0581 died that way while
// the journal only said `missing_result_event`. The schema uses no 2020-12-only
// keyword, so the pointer carries no constraint. Pack files generated by
// json-schema-text-from-zod.ts keep it; only this argv string omits it.
//
// Controller-owned fields (`input_hash`) are omitted from THIS worker-facing
// string by omitControllerOwnedResultFields — the same helper the generated
// pack/`autopilot schema` text uses; see its header for why. (`.omit()` is not
// allowed on a zod object with refinements, so the property is dropped from the
// generated JSON schema instead — it is never required.)
const { $schema: _metaSchema, ...zodResultJsonSchema } = z.toJSONSchema(
  AutopilotResultSchema,
) as Record<string, unknown>;
const autopilotResultJsonSchema = omitControllerOwnedResultFields(zodResultJsonSchema);
export const AUTOPILOT_RESULT_JSON_SCHEMA_STRING: string =
  JSON.stringify(autopilotResultJsonSchema);
